import { Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { CompetitionTeam } from './api'
import { useAllPlayers } from '../players/api'
import { useCompetitionId } from '../common/hooks'
import { Typography } from '../common/typography'
import CompareCard from '../stories/Compare/CompareCard'
import ComparePicker from '../stories/Compare/ComparePicker'
import classes from './Compare.module.css'
import CompareTable from '../stories/Compare/CompareTable'
import KpiTabsContainer from '../widgets/components/KpiTabsContainer'
import { Box, Flex, Group, Loader, Stack } from '@mantine/core'
import clsx from 'clsx'
import ViewSelector, { OptionKey } from '../stories/ViewSelector/ViewSelector'
import StatWithIndicator from '../stories/StatWithIndicator/StatWithIndicator'
import { Page } from '../widgets/Page'
import Meta from '../common/Meta'
import Header from '../common/components/header/Header'
import { useMetrics, useData } from '../widgets/api'
import { useWidgetContext } from '../widgets/providers'
import { useFmt } from '../widgets/format'
import CompareChart from './CompareChart'
import { Dropdown } from '../common/components/dropdown/Dropdown'

export type CompareSubject =
  | { type: 'team'; id: number; name: string; photo: string }
  | {
      type: 'user'
      id: number
      player_id: number
      name: string
      photo: string
      team_logo: string
    }
  | { type: 'overall'; id: number; name: string; photo: string }
  | null

function resolveSubject(
  type: string | null,
  id: string | null,
  teamsWithPlayers: CompetitionTeam[]
): CompareSubject {
  if (!type || !id) return null
  const numId = Number(id)
  if (isNaN(numId)) return null

  if (type === 'team') {
    const team = teamsWithPlayers.find((t) => t.team_id === numId)
    if (!team) return null
    return {
      type: 'team',
      id: team.team_id,
      name: team.team_name,
      photo: team.team_logo.main,
    }
  }

  if (type === 'user') {
    for (const team of teamsWithPlayers) {
      const player = team.players.find((p) => p.user_id === numId)
      if (player) {
        return {
          type: 'user',
          id: player.user_id ?? 0,
          player_id: player.player_id,
          name: player.player_name,
          photo: player.player_photo.main,
          team_logo: team.team_logo.mini,
        }
      }
    }
    return null
  }

  if (type === 'overall') {
    return { type: 'overall', id: 0, name: 'Total Business', photo: '' }
  }

  return null
}

export default function Compare() {
  const competition_id = useCompetitionId()
  const { data: players } = useAllPlayers({
    competition_id: Number(competition_id),
  })
  const teamsWithPlayers: CompetitionTeam[] = useMemo(() => {
    const teamsMap = new Map<number, CompetitionTeam>()

    players.items.forEach((player) => {
      if (!teamsMap.has(player.team_id)) {
        teamsMap.set(player.team_id, {
          team_id: player.team_id,
          team_name: player.team_name,
          team_logo: player.team_logo,
          players: [],
        })
      }

      teamsMap.get(player.team_id)!.players.push({
        player_id: player.player_id,
        user_id: player.user_id,
        player_name: player.name,
        player_photo: player.photo,
      })
    })

    return Array.from(teamsMap.values())
  }, [players])

  const [searchParams, setSearchParams] = useSearchParams()
  const [subject, setSubject] = useState<CompareSubject>(null)
  const [comparand, setComparand] = useState<CompareSubject>(null)

  // Restore subjects from URL once teamsWithPlayers is loaded
  const restoredFromUrl = useRef(false)
  useEffect(() => {
    if (restoredFromUrl.current || teamsWithPlayers.length === 0) return
    restoredFromUrl.current = true

    const left = resolveSubject(
      searchParams.get('ltype'),
      searchParams.get('lid'),
      teamsWithPlayers
    )
    const right = resolveSubject(
      searchParams.get('rtype'),
      searchParams.get('rid'),
      teamsWithPlayers
    )
    if (left) setSubject(left)
    if (right) setComparand(right)
  }, [teamsWithPlayers, searchParams])

  // Sync state → URL
  useEffect(() => {
    if (!restoredFromUrl.current) return
    const params: Record<string, string> = {}
    if (subject) {
      params.ltype = subject.type
      params.lid = String(subject.id)
    }
    if (comparand) {
      params.rtype = comparand.type
      params.rid = String(comparand.id)
    }
    setSearchParams(params, { replace: true })
  }, [subject, comparand, setSearchParams])
  const [subjectOpened, { open: subjectOpen, close: subjectClose }] =
    useDisclosure(false)
  const [comparandOpened, { open: comparandOpen, close: comparandClose }] =
    useDisclosure(false)
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const [view, setView] = useState<OptionKey>('line')
  const [component, setComponent] = useState<string>('percent')
  const components = [
    { label: 'Target (%)', value: 'percent' },
    { label: 'Actual', value: 'actual' },
    { label: 'Score YTD', value: 'score' },
  ]

  return (
    <Page
      path={'/compare'}
      context={{
        subject_type: 'overall',
        period_type: 'total',
        competition_id: competition_id,
      }}
    >
      <div className={classes.container}>
        <Meta title="Compare" />
        <Header title="Compare" />
        <div
          className={clsx(classes.performanceCompare, {
            [classes.performanceCompareMobile]: !isDesktop,
          })}
        >
          <div className={classes.subjectsWrapper}>
            {subject ? (
              <CompareCard
                backgroundColor="#DABAFF"
                color="#8073AD"
                direction="left"
                opened={subjectOpened}
                open={subjectOpen}
                close={subjectClose}
                teamWithPlayers={teamsWithPlayers}
                subject={subject}
                setSubject={setSubject}
                competitionId={competition_id}
              />
            ) : (
              <ComparePicker
                opened={subjectOpened}
                open={subjectOpen}
                close={subjectClose}
                teamWithPlayers={teamsWithPlayers}
                subject={subject}
                setSubject={setSubject}
              />
            )}

            {subject && comparand && (
              <div className={classes.versusContainer}>
                <Typography
                  fc={{ light: 'white', dark: 'white' }}
                  lh="125"
                  ta="center"
                  fw={700}
                  fs="xxxl"
                >
                  VS
                </Typography>
              </div>
            )}
            {comparand ? (
              <CompareCard
                backgroundColor="#F7F0EF"
                color="#B2675E"
                direction="right"
                opened={comparandOpened}
                open={comparandOpen}
                close={comparandClose}
                teamWithPlayers={teamsWithPlayers}
                subject={comparand}
                setSubject={setComparand}
                competitionId={competition_id}
              />
            ) : (
              <ComparePicker
                opened={comparandOpened}
                open={comparandOpen}
                close={comparandClose}
                teamWithPlayers={teamsWithPlayers}
                subject={comparand}
                setSubject={setComparand}
              />
            )}
          </div>

          <div>
            <KpiTabsContainer
              title="Compare Performance"
              id={'analysis_kpi_tabs'}
              subheading={
                <PerformanceHeading
                  subject={subject}
                  comparand={comparand}
                  view={view}
                  setView={setView}
                  component={component}
                  setComponent={setComponent}
                  isDesktop={isDesktop}
                  components={components}
                />
              }
            >
              <Suspense
                fallback={
                  <Stack w="full" h="400px" justify="center" align="center">
                    <Loader color="#0B6B7B" />
                  </Stack>
                }
              >
                {subject && comparand ? (
                  <div className={classes.compareDataContainer}>
                    <Flex
                      direction={isDesktop ? 'row' : 'column'}
                      gap="md"
                      align="stretch"
                      justify="stretch"
                    >
                      {(isDesktop || view === 'table') && (
                        <Box w={view === 'table' ? '100%' : '40%'}>
                          <CompareTable
                            subject={subject}
                            comparand={comparand}
                          />
                        </Box>
                      )}
                      {(isDesktop || view !== 'table') && (
                        <Box
                          style={{
                            backgroundColor:
                              'light-dark(white, var(--mantine-color-gray-9))',
                          }}
                          flex={1}
                          bdrs="md"
                          p="md"
                        >
                          <CompareChart
                            subject={subject}
                            comparand={comparand}
                            view={view}
                            component={component}
                          />
                        </Box>
                      )}
                    </Flex>
                  </div>
                ) : (
                  <div className={classes.noDataContainer}>
                    <Typography ta="center">No data yet.</Typography>
                    <Typography ta="center">
                      Select Players or Teams.
                    </Typography>
                  </div>
                )}
              </Suspense>
            </KpiTabsContainer>
          </div>
        </div>
      </div>
    </Page>
  )
}

function PerformanceHeading(props: {
  subject: CompareSubject
  comparand: CompareSubject
  view: OptionKey
  setView: (value: OptionKey) => void
  component: string
  setComponent: (value: string) => void
  isDesktop: boolean
  components: { label: string; value: string }[]
}) {
  const {
    subject,
    comparand,
    view,
    setView,
    component,
    setComponent,
    isDesktop,
    components,
  } = props

  return (
    <Stack>
      <Suspense>
        <div className={classes.compareDataContainer}>
          {subject && comparand && (
            <div className={classes.progressStatsContainer}>
              <CompareProgressBox isMain={true} subject={subject} />
              <CompareProgressBox isMain={false} subject={comparand} />
            </div>
          )}
        </div>
      </Suspense>
      <Block>
        <Group
          wrap="nowrap"
          align="center"
          justify={isDesktop ? 'flex-end' : 'flex-start'}
          gap="xs"
          pr="md"
          pl="md"
        >
          <ViewSelector
            value={view}
            onChange={setView}
            enabled={isDesktop ? ['line', 'bar'] : ['line', 'bar', 'table']}
          />
          <Dropdown
            placeholder="Select component"
            data={components}
            styles={{
              root: { maxWidth: '150px' },
              input: { backgroundColor: 'transparent' },
            }}
            value={component}
            onChange={(value) => setComponent(value || 'percent')}
            variant="default"
          />
        </Group>
      </Block>
    </Stack>
  )
}

function Block(props: { children: React.ReactNode; className?: string }) {
  return (
    <div className={clsx(classes.block, props.className)}>{props.children}</div>
  )
}

function CompareProgressBox(props: {
  subject: CompareSubject
  isMain: boolean
}) {
  const ctx = useWidgetContext()
  const { data: metrics } = useMetrics()

  // Get metric key from context
  const metricKey = ctx.metric || ''

  // Find the metric to get its name
  const metric = metrics.find((m) => m.key === metricKey)
  const title = `YTD ${metric?.name || ''}`

  const { data } = useData({
    period_type: 'total',
    period_range: false,
    periods: ['2025-01-01'],
    subject_type: props.subject?.type || 'overall',
    subject_ids: [props.subject?.id || 0],
    metrics: [metricKey, metricKey], // Repeat metric twice for 2 component/statistic pairs
    components: ['actual', 'percent'],
    statistics: ['value', 'value'],
    competition_id: ctx.competition_id,
  })

  // Find actual and percent values
  const actualValue = data.find((d) => d.component === 'actual')?.value || 0
  const percentValue = data.find((d) => d.component === 'percent')?.value || 0

  const valueFmt = useFmt({
    metric: metricKey,
    component: 'actual',
    statistic: 'value',
    value: actualValue,
  })

  return (
    <StatWithIndicator
      title={title}
      value={percentValue * 100}
      progress={percentValue * 100}
      subtitle={valueFmt}
      progressColor={props.isMain ? '#8073AD' : '#B2675E'}
    />
  )
}
